"use client";

import type React from "react";
import { useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";

interface ImportAgentProps {
  onAgentImported: () => void;
  buttonProps?: Record<string, unknown>;
}

export const ImportAgent: React.FC<ImportAgentProps> = ({
  onAgentImported,
  buttonProps = {},
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [agentId, setAgentId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { session } = useAuth();
  const toast = useToast();

  const handleClose = () => {
    setAgentId("");
    onClose();
  };

  const handleImport = async () => {
    const trimmed = agentId.trim();
    if (!trimmed) {
      toast({ title: "Enter an agent ID", status: "warning", duration: 3000, isClosable: true });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/voice-agents/agents/import`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.user?.id}`,
          },
          body: JSON.stringify({ agent_id: trimmed }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        toast({
          title: "Import failed",
          description: data.error || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Agent imported",
        description: `"${data.agent_name || trimmed}" is now in your account.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      handleClose();
      onAgentImported();
    } catch (err) {
      toast({
        title: "Import failed",
        description: "Network error — try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        leftIcon={<FontAwesomeIcon icon={faFileImport} />}
        onClick={onOpen}
        {...buttonProps}
      >
        Import Agent
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Existing Agent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Retell Agent ID</FormLabel>
              <Input
                placeholder="agent_xxxxxxxxxxxxxxxxxxxxxxxx"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void handleImport(); }}
                autoFocus
              />
              <FormHelperText>
                Find it in Retell → select your agent → copy the ID from the URL or agent settings.
              </FormHelperText>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="brandGradient"
              isLoading={isLoading}
              onClick={() => void handleImport()}
            >
              Import
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
